/**
 * Trunnion AI - Replay Timeline
 * Horizontal timeline with clickable segments for each execution step.
 * Segments are colored by status with a gold playhead indicator at the current step.
 */

import { useMemo } from 'react';
import { useReplayStore } from '@/stores/replayStore';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { ExecutionStep } from '@/types';

/**
 * Map execution status to a Tailwind background color class for timeline segments.
 */
function getSegmentColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'failed':
    case 'timed_out':
    case 'cancelled':
      return 'bg-red-500';
    case 'running':
    case 'waiting_approval':
      return 'bg-amber-500';
    default:
      return 'bg-trunnion-slate-500';
  }
}

/**
 * Compute a human-readable duration string between two ISO timestamps.
 */
function formatDuration(startedAt: string, completedAt?: string): string | null {
  if (!completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Truncate a label to a max length with ellipsis.
 */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

export function ReplayTimeline() {
  const steps = useReplayStore((s) => s.steps);
  const currentStepIndex = useReplayStore((s) => s.currentStepIndex);
  const seekTo = useReplayStore((s) => s.seekTo);

  const segments = useMemo(
    () =>
      steps.map((step: ExecutionStep, index: number) => ({
        index,
        label: step.agentName ?? step.nodeName,
        status: step.status,
        duration: formatDuration(step.startedAt, step.completedAt),
        statusLabel: EXECUTION_STATUS_CONFIG[step.status]?.label ?? step.status,
      })),
    [steps],
  );

  if (steps.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-trunnion-slate-400">
        No steps to display
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Segments row */}
      <div className="flex items-end gap-1">
        {segments.map((seg) => {
          const isActive = seg.index === currentStepIndex;

          return (
            <button
              key={seg.index}
              onClick={() => seekTo(seg.index)}
              title={`${seg.label} — ${seg.statusLabel}${seg.duration ? ` (${seg.duration})` : ''}`}
              className="group relative flex-1 min-w-0 flex flex-col items-center focus:outline-none"
            >
              {/* Playhead indicator (gold triangle) */}
              {isActive && (
                <div className="mb-1 flex justify-center">
                  <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[6px] border-t-trunnion-gold-500" />
                </div>
              )}

              {/* Segment bar */}
              <div
                className={`w-full h-3 rounded-sm transition-all ${getSegmentColor(seg.status)} ${
                  isActive
                    ? 'ring-2 ring-trunnion-gold-500 ring-offset-1 ring-offset-trunnion-slate-900 opacity-100'
                    : 'opacity-60 group-hover:opacity-90'
                }`}
              />

              {/* Duration badge */}
              {seg.duration && (
                <span className="mt-0.5 text-[10px] text-trunnion-slate-500">
                  {seg.duration}
                </span>
              )}

              {/* Label */}
              <span
                className={`mt-0.5 text-[10px] leading-tight text-center truncate w-full ${
                  isActive
                    ? 'text-trunnion-gold-400 font-medium'
                    : 'text-trunnion-slate-500 group-hover:text-trunnion-slate-300'
                }`}
              >
                {truncate(seg.label, 14)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Progress line beneath the segments */}
      <div className="mt-2 h-0.5 rounded-full bg-trunnion-slate-700 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-trunnion-gold-500 rounded-full transition-all duration-300"
          style={{
            width:
              steps.length > 1
                ? `${((currentStepIndex + 1) / steps.length) * 100}%`
                : '100%',
          }}
        />
      </div>
    </div>
  );
}
